import { Organization } from '@/data/Organization';
import { CurrentUser } from '@/data/users/CurrentUser';
import { KLAYO_COLORS } from '@/themes';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, PopoverOrigin } from '@mui/material';
import { Component } from 'react';
import { AppContext } from '../common/AppContext';
import { withLocale, WithLocaleProps } from '../providers/LocaleProvider';
import SwitchDemoIcon from '../resources/images/icons-cursor-click.svg';
import DeveloperIcon from '../resources/images/icons-developer.svg';
import LogOutIcon from '../resources/images/icons-logout.svg';
import SwitchToCompanyIcon from '../resources/images/icons-office.svg';
import SwitchToDemoDataIcon from '../resources/images/icons-play.svg';
import MyAccountIcon from '../resources/images/icons-user-account.svg';
import SwitchUserIcon from '../resources/images/icons-user-switch.svg';
import SwitchToMyCompanyIcon from '../resources/images/icons-users-1.svg';
import { isPreviewArmed } from '../views/try/mock/previewMode';

interface AppMenuProps extends WithLocaleProps {
  user?: CurrentUser | null;
  organization?: Organization | null;
  drawerMode?: boolean;
  devMode?: boolean;
  allowSwitchUser?: boolean;
  allowDemoSwitch?: boolean;
  showIcons?: boolean;
  anchorEl?: HTMLElement | null;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  open?: boolean;
  hideWithoutClosing?: boolean;
  onDevOptions?: (e: React.MouseEvent) => void;
  onSignOut?: (e: React.MouseEvent) => void;
  onSwitchUser?: (e: React.MouseEvent) => void;
  onDemoSwitch?: (e: React.MouseEvent, toDemo: boolean) => void;
  /** Enters/exits the Try It Now preview sandbox (demo data). Enter when not armed, exit when armed. */
  onDemoDataSwitch?: (e: React.MouseEvent) => void;
  allowDemoDataSwitch?: boolean;
  onMyAccount?: (e?: React.MouseEvent) => void;
  onClose?: (e?: {}) => void;
  onSettings?: (e?: React.MouseEvent) => void;
  allowSettings?: boolean;
  onNavigation?: (e?: React.MouseEvent) => void;
}

class AppMenuBase extends Component<AppMenuProps, any> {
  static contextType = AppContext;

  static defaultProps: Partial<AppMenuProps> = {
    allowDemoSwitch: false,
    drawerMode: false,
    showIcons: false,
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right',
    } as PopoverOrigin,
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    } as PopoverOrigin,
  };

  constructor(props: any) {
    super(props);
  }

  getItems() {
    const {
      user,
      organization,
      drawerMode,
      devMode,
      allowSwitchUser,
      onDevOptions,
      onSignOut,
      onSwitchUser,
      onDemoSwitch,
      allowDemoSwitch,
      onDemoDataSwitch,
      allowDemoDataSwitch,
      onMyAccount,
      onClose,
      showIcons,
      translate,
    } = this.props;

    const hasDevItems = devMode || allowSwitchUser;
    const hasMoreItems = allowDemoSwitch;
    const armed = isPreviewArmed();

    const items = [];

    if (hasDevItems) {
      if (allowSwitchUser) {
        items.push(
          <MenuItem
            key='switchuser'
            className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
            onClick={onSwitchUser}
          >
            {showIcons && (
              <ListItemIcon>
                <SwitchUserIcon />
              </ListItemIcon>
            )}
            <ListItemText disableTypography={true}>
              {translate('navigation.switchUser')}
            </ListItemText>
          </MenuItem>
        );
      }

      if (devMode) {
        items.push(
          <MenuItem
            key='dev'
            className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
            onClick={onDevOptions}
          >
            {showIcons && (
              <ListItemIcon>
                <DeveloperIcon />
              </ListItemIcon>
            )}
            <ListItemText disableTypography={true}>
              {translate('navigation.developer')}
            </ListItemText>
          </MenuItem>
        );
      }
    }

    if (onMyAccount) {
      items.push(
        <MenuItem
          key='myaccount'
          className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
          onClick={onMyAccount}
        >
          {showIcons && (
            <ListItemIcon>
              <MyAccountIcon />
            </ListItemIcon>
          )}
          <ListItemText disableTypography={true}>{translate('navigation.myAccount')}</ListItemText>
        </MenuItem>
      );
    }

    if (hasMoreItems) {
      if (allowDemoSwitch) {
        items.push(
          <MenuItem
            key='demoorg'
            className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
            onClick={(e) => onDemoSwitch?.(e, !organization?.isDemo)}
          >
            {showIcons && (
              <ListItemIcon>
                {organization && organization.isDemo ? <SwitchToCompanyIcon /> : <SwitchDemoIcon />}
              </ListItemIcon>
            )}
            <ListItemText disableTypography={true}>
              {organization && organization.isDemo
                ? translate('navigation.switchToYourCompany')
                : translate('navigation.switchToDemoCompany')}
            </ListItemText>
          </MenuItem>
        );
      }
    }

    if (allowDemoDataSwitch) {
      items.push(
        <MenuItem
          key='demodata'
          className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
          onClick={onDemoDataSwitch}
          sx={{
            '&:hover .MuiListItemIcon-root': {
              color: KLAYO_COLORS.WhiteWH900,
            },
          }}
        >
          {showIcons && (
            <ListItemIcon
              sx={{
                color: KLAYO_COLORS.NeonBlueNB500,
              }}
            >
              {armed ? <SwitchToMyCompanyIcon /> : <SwitchToDemoDataIcon />}
            </ListItemIcon>
          )}
          <ListItemText disableTypography={true}>
            {armed
              ? translate('navigation.switchToMyCompany')
              : translate('navigation.switchToDemoData')}
          </ListItemText>
        </MenuItem>
      );
    }

    // Single grey divider separating the switch/account actions from Sign out.
    if (items.length > 0) {
      items.push(<Divider key='div-signout' sx={{ margin: '8px 0 8px 0' }} />);
    }

    items.push(
      <MenuItem
        key='signout'
        className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
        onClick={onSignOut}
      >
        {showIcons && (
          <ListItemIcon>
            <LogOutIcon />
          </ListItemIcon>
        )}
        <ListItemText disableTypography={true}>{translate('navigation.signOut')}</ListItemText>
      </MenuItem>
    );

    return items;
  }

  renderItems() {
    const {
      user,
      organization,
      drawerMode,
      devMode,
      allowSwitchUser,
      onDevOptions,
      onSignOut,
      onSwitchUser,
      onDemoSwitch,
      allowDemoSwitch,
      onClose,
      translate,
    } = this.props;

    const hasDevItems = devMode || allowSwitchUser;
    const hasMoreItems = allowDemoSwitch;

    return (
      <>
        {hasDevItems && (
          <>
            {allowSwitchUser && (
              <MenuItem
                className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
                onClick={onSwitchUser}
              >
                <ListItemIcon>
                  <SwitchUserIcon />
                </ListItemIcon>
                <ListItemText disableTypography={true}>
                  {translate('navigation.switchUser')}
                </ListItemText>
              </MenuItem>
            )}

            {devMode && (
              <MenuItem
                className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
                onClick={onDevOptions}
              >
                <ListItemIcon>
                  <DeveloperIcon />
                </ListItemIcon>
                <ListItemText disableTypography={true}>
                  {translate('navigation.developer')}
                </ListItemText>
              </MenuItem>
            )}

            <Divider />
          </>
        )}

        {hasMoreItems && (
          <>
            {allowDemoSwitch && (
              <MenuItem
                className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
                onClick={(e) => onDemoSwitch && onDemoSwitch(e, !organization?.isDemo)}
              >
                <ListItemIcon>
                  {organization && organization.isDemo ? (
                    <SwitchToCompanyIcon />
                  ) : (
                    <SwitchDemoIcon />
                  )}
                </ListItemIcon>
                <ListItemText disableTypography={true}>
                  {organization && organization.isDemo
                    ? translate('navigation.switchToYourCompany')
                    : translate('navigation.switchToDemoCompany')}
                </ListItemText>
              </MenuItem>
            )}
            <Divider sx={{ margin: '8px 0 8px 0' }} />
          </>
        )}
        <MenuItem
          className={'klaro-app' + (drawerMode ? 'drawer' : 'menu') + '_menuitem'}
          onClick={onSignOut}
        >
          <ListItemIcon>
            <LogOutIcon />
          </ListItemIcon>
          <ListItemText disableTypography={true}>{translate('navigation.signOut')}</ListItemText>
        </MenuItem>
      </>
    );
  }

  render() {
    const {
      drawerMode,
      anchorEl,
      anchorOrigin,
      transformOrigin,
      onClose,
      open,
      hideWithoutClosing,
    } = this.props;

    return drawerMode ? (
      <div className='klaro-appdrawer-menu'>{this.getItems()}</div>
    ) : (
      <Menu
        className='klaro-appbar_usermenu'
        open={!!open}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin as any}
        transformOrigin={transformOrigin as any}
        onClose={onClose}
        sx={{ display: hideWithoutClosing ? 'none' : 'block' }}
      >
        {this.getItems()}
      </Menu>
    );
  }
}

export const AppMenu = withLocale(AppMenuBase);
